import type { StatusTone } from './tokens';
import { charCount } from './format';

/** One line of GET /logs/query (LogLine in @central/api). */
export interface LogLineView {
  at: string;
  service?: string;
  level?: string;
  line: string;
}

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/** Minimum-level choices; '' sends no level, which also keeps lines without one. */
export const logLevelOptions: Array<{ value: '' | LogLevel; label: string }> = [
  { value: '', label: 'All levels' },
  { value: 'debug', label: 'Debug and up' },
  { value: 'info', label: 'Info and up' },
  { value: 'warn', label: 'Warning and up' },
  { value: 'error', label: 'Errors only' },
];

const MINUTE = 60_000;

/** Time windows ending now. The server refuses a range over 30 days. */
export const logRangeOptions = [
  { value: '15m', label: 'Last 15 minutes', ms: 15 * MINUTE },
  { value: '1h', label: 'Last 1 hour', ms: 60 * MINUTE },
  { value: '6h', label: 'Last 6 hours', ms: 6 * 60 * MINUTE },
  { value: '24h', label: 'Last 24 hours', ms: 24 * 60 * MINUTE },
  { value: '7d', label: 'Last 7 days', ms: 7 * 24 * 60 * MINUTE },
  { value: '30d', label: 'Last 30 days', ms: 30 * 24 * 60 * MINUTE },
] as const;

export type LogRange = (typeof logRangeOptions)[number]['value'];

/** RFC 3339 start and end of a window ending at now. */
export function logRangeBounds(range: LogRange, now = Date.now()): { start: string; end: string } {
  const ms = logRangeOptions.find((r) => r.value === range)!.ms;
  return { start: new Date(now - ms).toISOString(), end: new Date(now).toISOString() };
}

/** How long log lines are kept, in the words both apps show. */
export const logRetention = '30 days retention';

const SERVICE = /^[A-Za-z0-9_.-]{1,64}$/;
export const LOG_CONTAINS_MAX = 256;

/**
 * Field errors for the free-text filters, matching the server's limits: a
 * service label of 1–64 of [A-Za-z0-9_.-], and one line of at most 256
 * characters to find. Empty values are no filter.
 */
export function logFilterErrors(f: { service: string; contains: string }): {
  service?: string;
  contains?: string;
} {
  const out: { service?: string; contains?: string } = {};
  if (f.service && !SERVICE.test(f.service))
    out.service = 'Letters, digits, dot, dash or underscore; up to 64.';
  if (charCount(f.contains) > LOG_CONTAINS_MAX)
    out.contains = `At most ${LOG_CONTAINS_MAX} characters.`;
  else if (/[\r\n]/.test(f.contains)) out.contains = 'One line only.';
  return out;
}

/** Chip tone for a level label; unknown levels are neutral. */
export const levelTone = (level: string): StatusTone => {
  const l = level.toLowerCase();
  if (['error', 'err', 'fatal', 'critical', 'panic'].includes(l)) return 'down';
  if (l === 'warn' || l === 'warning') return 'warn';
  return 'unk';
};

const pad = (n: number, w = 2) => String(n).padStart(w, '0');

/** "10:41:52.118" for a time on today's UTC date, else "Oct 20 23:51:02". UTC throughout. */
export function formatLogTime(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const time = `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
  if (d.toISOString().slice(0, 10) === now.toISOString().slice(0, 10))
    return `${time}.${pad(d.getUTCMilliseconds(), 3)}`;
  const day = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return `${day} ${time}`;
}

/** Plain-text export: one "time service level line" row per log line, as listed. */
export function logLinesText(lines: LogLineView[]): string {
  return lines
    .map((l) => [l.at, l.service ?? '-', l.level ?? '-', l.line].join('\t'))
    .join('\n')
    .concat(lines.length ? '\n' : '');
}

/** Saves text as a file through a temporary object URL. */
export function downloadText(filename: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
